"use client";

import { CenterOptions } from "@/Helpers/CenterOptions";
import { AlertsSectionProps } from "@/Templates/SectionsProps";
import { FC, ReactElement } from "react";
import HomeEightSliderOneGenerated, {
  HomeEightSliderOneGeneratedProps,
} from "../Sections/_generated/HomeEightSliderOne.generated";
import HomeEighteenSliderSectionGenerated, {
  HomeEighteenSliderSectionGeneratedProps,
} from "../Sections/_generated/HomeEighteenSliderSection.generated";
import HomeFifteenStartSliderSectionGenerated, {
  HomeFifteenStartSliderSectionGeneratedProps,
} from "../Sections/_generated/HomeFifteenStartSliderSection.generated";
import HomeFiveSliderSectionGenerated, {
  HomeFiveSliderSectionGeneratedProps,
} from "../Sections/_generated/HomeFiveSliderSection.generated";
import HomeFourSliderSectionGenerated, {
  HomeFourSliderSectionGeneratedProps,
} from "../Sections/_generated/HomeFourSliderSection.generated";
import HomeFourteenStartSliderSectionGenerated, {
  HomeFourteenStartSliderSectionGeneratedProps,
} from "../Sections/_generated/HomeFourteenStartSliderSection.generated";
import HomeOneServicesSectionGenerated, {
  HomeOneServicesSectionGeneratedProps,
} from "../Sections/_generated/HomeOneServicesSection.generated";
import HomeSevenSliderSectionGenerated, {
  HomeSevenSliderSectionGeneratedProps,
} from "../Sections/_generated/HomeSevenSliderSection.generated";
import HomeSeventeenSliderSectionGenerated, {
  HomeSeventeenSliderSectionGeneratedProps,
} from "../Sections/_generated/HomeSeventeenSliderSection.generated";
import HomeSixSlideSectionGenerated, {
  HomeSixSlideSectionGeneratedProps,
} from "../Sections/_generated/HomeSixSlideSection.generated";
import HomeSixteenSliderSectionGenerated, {
  HomeSixteenSliderSectionGeneratedProps,
} from "../Sections/_generated/HomeSixteenSliderSection.generated";
import HomeTenSliderSectionGenerated, {
  HomeTenSliderSectionGeneratedProps,
} from "../Sections/_generated/HomeTenSliderSection.generated";
import HomeTwoSliderSectionGenerated, {
  HomeTwoSliderSectionGeneratedProps,
} from "../Sections/_generated/HomeTwoSliderSection.generated";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";

type GeneratedAlertsRenderer = (props: AlertsSectionProps) => ReactElement;
type GeneratedAlertsProps = Record<string, unknown>;
type GeneratedAlertsItem = Record<string, unknown>;

const AlertsAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateAlertsAppearance;

const textKeyCandidates = [
  "h30Text",
  "h20Text",
  "h10Text",
  "link0Text",
  "a0Text",
];

function buildGeneratedAlertsProps<T extends GeneratedAlertsProps>(
  sectionKey: string,
  overrides?: Partial<T>,
): T {
  return buildGeneratedSectionProps<T>(sectionKey, overrides);
}

function getAlertsTextKey(item: GeneratedAlertsItem): string | undefined {
  return textKeyCandidates.find((key) => key in item);
}

function isAlertsCollectionItem(item: GeneratedAlertsItem): boolean {
  return !!getAlertsTextKey(item);
}

function applyAlertsCollection(
  defaults: GeneratedAlertsItem[],
  items: AlertsSectionProps["lists"]["Default"],
): GeneratedAlertsItem[] {
  if (items.length === 0) {
    return [];
  }

  const textIndexes = defaults
    .map((item, index) => ({
      index,
      textKey: getAlertsTextKey(item),
    }))
    .filter(
      (item): item is { index: number; textKey: string } => !!item.textKey,
    );
  const hasMixedStructure =
    textIndexes.length > 0 && textIndexes.length < defaults.length;

  if (hasMixedStructure) {
    const textTemplate = textIndexes[0]
      ? defaults[textIndexes[0].index]
      : undefined;
    const separatorTemplate = defaults.find(
      (item, index) =>
        !textIndexes.some((textItem) => textItem.index === index),
    );

    return items.flatMap((listItem, index) => {
      const templateInfo = textIndexes[index % textIndexes.length];
      const baseTextItem = templateInfo
        ? defaults[templateInfo.index]
        : (textTemplate ?? {});
      const textKey = getAlertsTextKey(baseTextItem);
      const nextItemsForListItem: GeneratedAlertsItem[] = [
        {
          ...baseTextItem,
          key: `${listItem.Id}-text`,
          ...(textKey ? { [textKey]: listItem.Content } : {}),
        },
      ];

      if (separatorTemplate) {
        nextItemsForListItem.push({
          ...separatorTemplate,
          key: `${listItem.Id}-separator`,
        });
      }

      return nextItemsForListItem;
    });
  }

  return items.map((listItem, index) => {
    const baseItem = defaults[index % defaults.length] ?? {};
    const textKey = getAlertsTextKey(baseItem);
    const nextItem: GeneratedAlertsItem = {
      ...baseItem,
      key: listItem.Id,
    };

    if (textKey) {
      nextItem[textKey] = listItem.Content;
    }

    return nextItem;
  });
}

function applyAlertsOverrides<T extends GeneratedAlertsProps>(
  generatedProps: T,
  props: AlertsSectionProps,
): T {
  const nextProps: GeneratedAlertsProps = {
    ...generatedProps,
  };

  const collectionKeys = Object.keys(nextProps).filter((key) => {
    const value = nextProps[key];
    return (
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === "object" &&
      value[0] !== null &&
      isAlertsCollectionItem(value[0] as GeneratedAlertsItem)
    );
  });

  const alertItems = props.lists.Default ?? [];
  let offset = 0;
  collectionKeys.forEach((key, index) => {
    const defaults = nextProps[key] as GeneratedAlertsItem[];
    const isLastCollection = index === collectionKeys.length - 1;
    const nextItems = isLastCollection
      ? alertItems.slice(offset)
      : alertItems.slice(offset, offset + defaults.length);

    nextProps[key] = applyAlertsCollection(defaults, nextItems);
    offset += nextItems.length;
  });

  return nextProps as T;
}

const generatedAlertsRenderers: Record<string, GeneratedAlertsRenderer> = {
  [AlertsAppearance.Home1.key]: (props) => (
    <HomeOneServicesSectionGenerated
      {...applyAlertsOverrides(
        buildGeneratedAlertsProps<HomeOneServicesSectionGeneratedProps>(
          "HomeOneServicesSection",
        ),
        props,
      )}
    />
  ),
  [AlertsAppearance.Home2.key]: (props) => (
    <HomeTwoSliderSectionGenerated
      {...applyAlertsOverrides(
        buildGeneratedAlertsProps<HomeTwoSliderSectionGeneratedProps>(
          "HomeTwoSliderSection",
        ),
        props,
      )}
    />
  ),
  [AlertsAppearance.Home4.key]: (props) => (
    <HomeFourSliderSectionGenerated
      {...applyAlertsOverrides(
        buildGeneratedAlertsProps<HomeFourSliderSectionGeneratedProps>(
          "HomeFourSliderSection",
        ),
        props,
      )}
    />
  ),
  [AlertsAppearance.Home5.key]: (props) => (
    <HomeFiveSliderSectionGenerated
      {...applyAlertsOverrides(
        buildGeneratedAlertsProps<HomeFiveSliderSectionGeneratedProps>(
          "HomeFiveSliderSection",
        ),
        props,
      )}
    />
  ),
  [AlertsAppearance.Home6.key]: (props) => (
    <HomeSixSlideSectionGenerated
      {...applyAlertsOverrides(
        buildGeneratedAlertsProps<HomeSixSlideSectionGeneratedProps>(
          "HomeSixSlideSection",
        ),
        props,
      )}
    />
  ),
  [AlertsAppearance.Home7.key]: (props) => (
    <HomeSevenSliderSectionGenerated
      {...applyAlertsOverrides(
        buildGeneratedAlertsProps<HomeSevenSliderSectionGeneratedProps>(
          "HomeSevenSliderSection",
        ),
        props,
      )}
    />
  ),
  [AlertsAppearance.Home8.key]: (props) => (
    <HomeEightSliderOneGenerated
      {...applyAlertsOverrides(
        buildGeneratedAlertsProps<HomeEightSliderOneGeneratedProps>(
          "HomeEightSliderOne",
        ),
        props,
      )}
    />
  ),
  [AlertsAppearance.Home10.key]: (props) => (
    <HomeTenSliderSectionGenerated
      {...applyAlertsOverrides(
        buildGeneratedAlertsProps<HomeTenSliderSectionGeneratedProps>(
          "HomeTenSliderSection",
        ),
        props,
      )}
    />
  ),
  [AlertsAppearance.Home14.key]: (props) => (
    <HomeFourteenStartSliderSectionGenerated
      {...applyAlertsOverrides(
        buildGeneratedAlertsProps<HomeFourteenStartSliderSectionGeneratedProps>(
          "HomeFourteenStartSliderSection",
        ),
        props,
      )}
    />
  ),
  [AlertsAppearance.Home15.key]: (props) => (
    <HomeFifteenStartSliderSectionGenerated
      {...applyAlertsOverrides(
        buildGeneratedAlertsProps<HomeFifteenStartSliderSectionGeneratedProps>(
          "HomeFifteenStartSliderSection",
        ),
        props,
      )}
    />
  ),
  [AlertsAppearance.Home16.key]: (props) => (
    <HomeSixteenSliderSectionGenerated
      {...applyAlertsOverrides(
        buildGeneratedAlertsProps<HomeSixteenSliderSectionGeneratedProps>(
          "HomeSixteenSliderSection",
          {
            sliderSectionServicesSlidesItemWrapper: (item, content) => {
              const iconClassName =
                typeof item.i0ClassName === "string"
                  ? item.i0ClassName
                  : undefined;
              const spanClassName =
                typeof item.span0ClassName === "string"
                  ? item.span0ClassName
                  : undefined;
              const divClassName =
                typeof item.divClassName === "string"
                  ? item.divClassName
                  : "services-slide";

              if (!iconClassName) {
                return content;
              }

              return (
                <div className={divClassName} key={item.key}>
                  <span className={spanClassName}>
                    <i className={iconClassName} />
                  </span>
                </div>
              );
            },
          },
        ),
        props,
      )}
    />
  ),
  [AlertsAppearance.Home17.key]: (props) => (
    <HomeSeventeenSliderSectionGenerated
      {...applyAlertsOverrides(
        buildGeneratedAlertsProps<HomeSeventeenSliderSectionGeneratedProps>(
          "HomeSeventeenSliderSection",
        ),
        props,
      )}
    />
  ),
  [AlertsAppearance.Home18.key]: (props) => (
    <HomeEighteenSliderSectionGenerated
      {...applyAlertsOverrides(
        buildGeneratedAlertsProps<HomeEighteenSliderSectionGeneratedProps>(
          "HomeEighteenSliderSection",
        ),
        props,
      )}
    />
  ),
};

const GeneratedAlertsSection: FC<AlertsSectionProps> = (props) => {
  return renderGeneratedSection(
    props,
    generatedAlertsRenderers,
    AlertsAppearance.Home1.key,
    (sectionProps) => sectionProps.setting.Appearance?.Key,
  );
};

export default GeneratedAlertsSection;
